import { EditIcon, MMButton, RecurringStatusBadge, ViewIcon } from '@components'
import cn from '@integrations/classnames'
import { formatDisplayDate, getRecurringFrequencyDescription } from '@utils'
import { RecurringPlanStatuses, useCurrencies } from 'mmfintech-backend-api'
import type { PortalRecurringPlanView } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import { MMTableView } from 'mmfintech-portal-commons'

interface Props {
  recurringPlans: PortalRecurringPlanView[]
  onView?: (plan: PortalRecurringPlanView) => void
  onEdit?: (plan: PortalRecurringPlanView) => void
  onCheckPastTransactions?: (plan: PortalRecurringPlanView) => void
}

function RecurringPaymentsTable({
  recurringPlans,
  onView = () => {},
  onEdit = () => {},
  onCheckPastTransactions = () => {}
}: Props) {
  const { getCurrencyPrecision } = useCurrencies()

  return (
    <MMTableView.Table>
      <MMTableView.Head>
        <MMTableView.Row>
          <MMTableView.HeadCell>{tr('FRONTEND.RECURRING.COLUMNS.NAME', 'Recurring name')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.RECURRING.COLUMNS.FREQUENCY', 'Frequency')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>
            {tr('FRONTEND.RECURRING.COLUMNS.NEXT_EXECUTION', 'Next execution')}
          </MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.RECURRING.COLUMNS.RECIPIENT', 'Recipient')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.RECURRING.COLUMNS.ACCOUNT', 'Account')}</MMTableView.HeadCell>
          <MMTableView.HeadCell className={cn('thin')}>
            {tr('FRONTEND.RECURRING.COLUMNS.STATUS', 'Status')}
          </MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.RECURRING.COLUMNS.AMOUNT', 'Amount')}</MMTableView.HeadCell>
          <MMTableView.HeadCell className={cn('buttons')}>
            <span className={cn('visually-hidden')}>{tr('FRONTEND.RECURRING.COLUMNS.ACTIONS', 'Actions')}</span>
          </MMTableView.HeadCell>
        </MMTableView.Row>
      </MMTableView.Head>

      <MMTableView.Body>
        {recurringPlans.map((plan) => {
          const {
            id,
            recurringPlanName,
            startDate,
            recurringStep,
            recurringPlanStatus,
            nextBillingDate,
            recurringAmount,
            currency,
            recipient,
            accountName
          } = plan

          return (
            <MMTableView.Row key={id} data-test='recurring-payments-row'>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.NAME', 'Recurring name')}>
                {recurringPlanName}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.FREQUENCY', 'Frequency')}>
                {getRecurringFrequencyDescription(startDate, recurringStep)}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.NEXT_EXECUTION', 'Next execution')}>
                {recurringPlanStatus === RecurringPlanStatuses.ACTIVE && nextBillingDate
                  ? formatDisplayDate(nextBillingDate)
                  : '—'}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.RECIPIENT', 'Recipient')}>
                {recipient}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.ACCOUNT', 'Account')}>
                {accountName}
              </MMTableView.Cell>
              <MMTableView.Cell
                caption={tr('FRONTEND.RECURRING.COLUMNS.STATUS', 'Status')}
                className={cn('status-cell')}
              >
                <RecurringStatusBadge status={recurringPlanStatus} />
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.RECURRING.COLUMNS.AMOUNT', 'Amount')}>
                <span className={cn('amount')}>
                  {formatMoney(recurringAmount, currency, getCurrencyPrecision(currency))}
                </span>
              </MMTableView.Cell>
              <MMTableView.Cell className={cn('preview-cell', 'no-border', 'row-actions-cell')}>
                <div className={cn('table-row-actions')}>
                  <MMButton
                    type='button'
                    size='small'
                    variant='round-secondary'
                    collapsed
                    collapsedIcon={ViewIcon}
                    title={tr('FRONTEND.RECURRING.ACTIONS.VIEW', 'View')}
                    onClick={() => onView(plan)}
                    data-test='button-view-recurring'
                  />
                  <MMButton
                    type='button'
                    size='small'
                    variant='round-secondary'
                    collapsed
                    collapsedIcon={EditIcon}
                    title={tr('FRONTEND.RECURRING.ACTIONS.EDIT', 'Edit')}
                    onClick={() => onEdit(plan)}
                    data-test='button-edit-recurring'
                  />
                  <MMButton
                    type='button'
                    size='small'
                    variant='secondary'
                    caption={tr('FRONTEND.RECURRING.ACTIONS.CHECK_PAST_TRANSACTIONS', 'Check past transactions')}
                    onClick={() => onCheckPastTransactions(plan)}
                    data-test='button-check-past-transactions'
                  />
                </div>
              </MMTableView.Cell>
            </MMTableView.Row>
          )
        })}
      </MMTableView.Body>
    </MMTableView.Table>
  )
}

export default RecurringPaymentsTable
